import { Link } from 'react-router-dom';
import { useSEO } from '@/hooks/useSEO';
import { useTranslation } from 'react-i18next';

const AboutPage = () => {
  const { t } = useTranslation();

  useSEO({
    title: 'Over Ons | ZpiritualZ Den Haag – Sjamaanse Tradities & Hing Bescherming',
    description: 'Leer meer over ZpiritualZ in Den Haag: opgericht in 2018 vanuit eeuwenoude Surinaamse en Hindoestaanse hing-tradities. Onze missie is spirituele bescherming toegankelijk maken voor iedereen.',
    canonical: 'https://www.zpiritualz.com/about',
    keywords: 'ZpiritualZ over ons, hing traditie Suriname, sjamanisme bescherming, asafoetida spiritueel, spirituele webshop Den Haag opgericht 2018',
    ogImage: 'https://public.readdy.ai/ai/img_res/863b28ff74304d37e40cc442ba053611.jpg',
    jsonLd: {
      '@context': 'https://schema.org',
      '@type': 'AboutPage',
      name: 'Over ZpiritualZ',
      url: 'https://www.zpiritualz.com/about',
      description: 'ZpiritualZ is opgericht in 2018 vanuit eeuwenoude Surinaamse en Hindoestaanse hing-tradities.',
      mainEntity: {
        '@type': 'Organization',
        name: 'ZpiritualZ',
        url: 'https://www.zpiritualz.com',
        foundingDate: '2018',
      },
    },
  });

  const timelineItems = [
    { year: '1800s', title: t('about_tl1_title', 'Hing arriveert in Suriname'), desc: t('about_tl1_desc', 'Met de komst van Hindoestaanse contractarbeiders vanuit India naar Suriname reist ook de hing-traditie mee.') },
    { year: '1950s', title: t('about_tl2_title', 'Van India naar de Diaspora'), desc: t('about_tl2_desc', 'Generatie op generatie dragen Surinaams-Hindoestaanse families de kennis van hing over.') },
    { year: '2018', title: t('about_tl3_title', 'De Geboorte van ZpiritualZ'), desc: t('about_tl3_desc', 'Vanuit een persoonlijke ervaring met een negatieve omgeving groeit het idee om beschermende wijsheid in moderne producten te gieten.') },
    { year: '2021', title: t('about_tl4_title', 'Eerste Productlijn Gelanceerd'), desc: t('about_tl4_desc', 'De Evil Energy Destroy Serie wordt geboren: zeep, pasta, poeder, olie, spray en cologne.') },
    { year: '2024', title: t('about_tl5_title', 'Blue Protect Uitbreiding'), desc: t('about_tl5_desc', 'Met de Blue Protect Serie wordt ook het beschermende kracht van Indigofera-extract geïntroduceerd.') },
  ];

  const hingFacts = [
    { icon: 'ri-map-pin-2-line', title: t('about_hing_origin'), desc: t('about_hing_origin_desc') },
    { icon: 'ri-shield-star-line', title: t('about_hing_protector'), desc: t('about_hing_protector_desc') },
    { icon: 'ri-fire-line', title: t('about_hing_smoke'), desc: t('about_hing_smoke_desc') },
    { icon: 'ri-heart-3-line', title: t('about_hing_baby'), desc: t('about_hing_baby_desc') },
  ];

  const philosophy = [
    { icon: 'ri-seedling-line', title: t('about_phil_roots'), desc: t('about_phil_roots_desc') },
    { icon: 'ri-flask-line', title: t('about_phil_pure'), desc: t('about_phil_pure_desc') },
    { icon: 'ri-compass-3-line', title: t('about_phil_sham'), desc: t('about_phil_sham_desc') },
    { icon: 'ri-home-heart-line', title: t('about_phil_ritual'), desc: t('about_phil_ritual_desc') },
  ];

  const shamPillars = [
    { icon: 'ri-earth-line', title: t('about_sham_worldwide'), desc: t('about_sham_worldwide_desc') },
    { icon: 'ri-timer-line', title: t('about_sham_age'), desc: t('about_sham_age_desc') },
    { icon: 'ri-leaf-line', title: t('about_sham_plants'), desc: t('about_sham_plants_desc') },
  ];

  const hingJourney = [
    { place: t('about_journey_iran', 'Iran & Afghanistan'), role: t('about_journey_iran_role', 'Herkomst'), desc: t('about_journey_iran_desc', 'Ferula asafoetida groeit in de droge berggebieden van Centraal-Azië. De hars wordt al meer dan 2.000 jaar verzameld.') },
    { place: t('about_journey_india', 'India'), role: t('about_journey_india_role', 'Verspreiding'), desc: t('about_journey_india_desc', 'In de Ayurvedische geneeskunde en hindoeïstische rituelen speelt hing een centrale rol als beschermer en reiniger.') },
    { place: t('about_journey_suriname', 'Suriname'), role: t('about_journey_suriname_role', 'Diaspora'), desc: t('about_journey_suriname_desc', 'Via de Hindoestaanse contractarbeiders (1873–1916) komt hing in Suriname terecht en wordt diep verankerd in de cultuur.') },
    { place: t('about_journey_nl', 'Nederland'), role: t('about_journey_nl_role', 'Vandaag'), desc: t('about_journey_nl_desc', 'ZpiritualZ brengt deze traditie naar de Nederlandse markt — voor iedereen die de kracht van hing wil ervaren.') },
  ];

  return (
    <div className="min-h-screen bg-[#08080f] pt-20">
      {/* Hero */}
      <section className="relative py-32 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=ancient%20mystical%20aromatic%20resin%20burning%20ritual%20smoke%20ceremony%20dark%20atmospheric%20sacred%20space%20with%20golden%20candlelight%20and%20dried%20herbs%20spiritual%20protection%20ritual%20moody%20cinematic%20photography&width=1920&height=800&seq=zpiritual-about-hero&orientation=landscape"
            alt="ZpiritualZ Verhaal"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#08080f]/70 via-[#08080f]/55 to-[#08080f]" />
        </div>
        <div className="relative z-10 max-w-[1440px] mx-auto px-6 lg:px-12 text-center">
          <p className="text-[#d4a017] text-xs font-semibold uppercase tracking-widest mb-4">{t('about_badge')}</p>
          <h1 className="text-5xl lg:text-7xl font-bold text-white mb-6 leading-tight">
            {t('about_hero_title')}
          </h1>
          <div className="max-w-2xl mx-auto mb-8 px-6 py-5 rounded-2xl bg-white/5 border border-teal-400/25 backdrop-blur-sm">
            <p className="text-teal-300 text-xs uppercase tracking-widest font-bold mb-2">{t('hero_shamBadge')}</p>
            <p className="text-white/85 text-base lg:text-lg leading-relaxed">
              <strong className="text-teal-300 font-bold">{t('hero_shamLabel')}</strong> {t('hero_shamDesc')}
            </p>
          </div>
          <p className="text-white/60 text-base lg:text-lg leading-relaxed max-w-xl mx-auto mb-10">
            {t('about_sub')}
          </p>
          <div className="flex flex-wrap items-center justify-center gap-10">
            <div className="text-center">
              <p className="text-4xl font-bold text-transparent bg-gradient-to-r from-teal-400 to-[#d4a017] bg-clip-text">2018</p>
              <p className="text-white/40 text-xs mt-1 uppercase tracking-wider">{t('about_founded')}</p>
            </div>
            <div className="h-10 w-px bg-white/10 hidden sm:block" />
            <div className="text-center">
              <p className="text-4xl font-bold text-transparent bg-gradient-to-r from-teal-400 to-[#d4a017] bg-clip-text">2.000+</p>
              <p className="text-white/40 text-xs mt-1 uppercase tracking-wider">{t('about_tradition')}</p>
            </div>
            <div className="h-10 w-px bg-white/10 hidden sm:block" />
            <div className="text-center">
              <p className="text-4xl font-bold text-transparent bg-gradient-to-r from-teal-400 to-[#d4a017] bg-clip-text">800+</p>
              <p className="text-white/40 text-xs mt-1 uppercase tracking-wider">{t('about_households')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Founding Story */}
      <section className="py-24 max-w-[1440px] mx-auto px-6 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div className="relative">
            <img
              src="https://readdy.ai/api/search-image?query=close%20up%20of%20raw%20asafoetida%20hing%20resin%20chunks%20on%20dark%20stone%20surface%20with%20dried%20herbs%20and%20traditional%20glass%20bottles%20amber%20golden%20tones%20mystical%20apothecary%20dark%20background%20moody%20still%20life&width=700&height=600&seq=zpiritual-story-img&orientation=landscape"
              alt="De Hing Traditie"
              className="w-full h-[420px] object-cover object-top rounded-3xl"
            />
            <div className="absolute -bottom-6 -right-6 bg-[#d4a017]/10 border border-[#d4a017]/30 rounded-2xl p-5 max-w-[220px]">
              <p className="text-[#d4a017] text-xs uppercase tracking-widest mb-1">{t('about_ingredient_label')}</p>
              <p className="text-white font-bold text-lg">{t('about_ingredient_name')}</p>
              <p className="text-white/50 text-xs mt-1">{t('about_ingredient_sub')}</p>
            </div>
          </div>
          <div>
            <p className="text-[#d4a017] text-xs font-semibold uppercase tracking-widest mb-4">{t('about_story_badge')}</p>
            <h2 className="text-4xl font-bold text-white mb-6 leading-tight">
              {t('about_story_title')}
            </h2>
            <p className="text-white/65 leading-relaxed mb-5 text-base">{t('about_story_p1')}</p>
            <p className="text-white/65 leading-relaxed mb-5 text-base">{t('about_story_p2')}</p>
            <p className="text-white/65 leading-relaxed text-base">{t('about_story_p3')}</p>
          </div>
        </div>
      </section>

      {/* Sjamanisme Banner */}
      <section className="py-20 bg-gradient-to-br from-teal-950/60 via-[#08080f] to-[#0b0b18]">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-teal-400 text-xs font-bold uppercase tracking-widest mb-4">{t('about_sham_origin')}</p>
              <h2 className="text-4xl lg:text-5xl font-bold text-white mb-6 leading-tight">
                <span className="text-transparent bg-gradient-to-r from-teal-300 to-[#d4a017] bg-clip-text">{t('hero_shamLabel')}</span>
                <br />{t('about_sham_title')}
              </h2>
              <div className="p-5 rounded-2xl bg-teal-500/8 border border-teal-400/25 mb-8">
                <p className="text-white/85 text-base leading-relaxed">
                  <strong className="text-teal-300">{t('hero_shamLabel')}</strong> {t('about_sham_desc')}
                </p>
              </div>
              <div className="space-y-4">
                {shamPillars.map(item => (
                  <div key={item.title} className="flex gap-4 items-start">
                    <div className="w-9 h-9 flex items-center justify-center shrink-0 rounded-xl bg-teal-500/15 border border-teal-500/25">
                      <i className={`${item.icon} text-teal-400 text-base`} />
                    </div>
                    <div>
                      <p className="text-white font-semibold text-sm mb-0.5">{item.title}</p>
                      <p className="text-white/45 text-xs leading-relaxed">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <img
                src="https://readdy.ai/api/search-image?query=ancient%20shamanic%20ritual%20ceremony%20indigenous%20tribal%20shaman%20performing%20spiritual%20healing%20fire%20ceremony%20smoke%20sacred%20herbs%20burning%20dark%20dramatic%20atmospheric%20cinematic%20photography%20ancient%20wisdom%20powerful%20mystical%20presence%20earth%20tones%20deep%20shadows%20golden%20fire%20glow&width=700&height=600&seq=sjamanisme-about1&orientation=landscape"
                alt="Sjamanisme ritueel"
                className="w-full h-[480px] object-cover object-top rounded-3xl"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#08080f]/70 via-transparent to-transparent rounded-3xl" />
              <div className="absolute bottom-6 left-6 right-6 p-4 rounded-xl bg-black/40 backdrop-blur-sm border border-teal-400/20">
                <p className="text-teal-300 font-bold text-sm mb-1">{t('about_sham_caption')}</p>
                <p className="text-white/60 text-xs leading-relaxed">{t('about_sham_caption_sub')}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Hing Traditie */}
      <section className="py-24 bg-gradient-to-b from-[#0b0b18] to-[#08080f]">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <p className="text-[#d4a017] text-xs font-semibold uppercase tracking-widest mb-4">{t('about_hing_badge')}</p>
            <h2 className="text-4xl lg:text-5xl font-bold text-white mb-5">
              {t('about_hing_title')}
            </h2>
            <p className="text-white/55 text-base max-w-2xl mx-auto leading-relaxed">{t('about_hing_sub')}</p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center mb-20">
            <div className="space-y-5">
              {hingFacts.map(fact => (
                <div key={fact.title} className="flex gap-4 p-5 bg-white/3 border border-white/8 rounded-2xl hover:border-teal-500/20 transition-colors">
                  <div className="w-10 h-10 flex items-center justify-center shrink-0 mt-0.5">
                    <i className={`${fact.icon} text-xl text-[#d4a017]`} />
                  </div>
                  <div>
                    <h3 className="text-white font-semibold text-base mb-1">{fact.title}</h3>
                    <p className="text-white/50 text-sm leading-relaxed">{fact.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="relative h-full">
              <img
                src="https://readdy.ai/api/search-image?query=traditional%20Indian%20Surinamese%20spiritual%20ritual%20ceremony%20with%20hing%20asafoetida%20burning%20smoke%20incense%20sacred%20objects%20brass%20bowls%20flowers%20offerings%20dark%20warm%20atmospheric%20photography%20cultural%20heritage&width=700&height=600&seq=hing-tradition-img&orientation=landscape"
                alt="Hing Ritueel"
                className="w-full h-[460px] object-cover object-top rounded-3xl"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#08080f]/60 via-transparent to-transparent rounded-3xl" />
              <div className="absolute bottom-6 left-6 right-6">
                <p className="text-white/90 text-sm italic leading-relaxed">{t('about_hing_quote')}</p>
              </div>
            </div>
          </div>

          {/* Culturele Route */}
          <div className="bg-white/3 border border-white/8 rounded-3xl p-8 lg:p-12">
            <h3 className="text-white font-bold text-2xl mb-8 text-center">{t('about_hing_journey')}</h3>
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
              {hingJourney.map((stop, i, arr) => (
                <div key={stop.place} className="flex flex-col sm:flex-row items-start sm:items-center gap-4 flex-1">
                  <div className="flex-1">
                    <p className="text-[#d4a017] text-xs uppercase tracking-widest mb-1">{stop.role}</p>
                    <p className="text-white font-bold text-base mb-1">{stop.place}</p>
                    <p className="text-white/45 text-xs leading-relaxed">{stop.desc}</p>
                  </div>
                  {i < arr.length - 1 && (
                    <div className="w-8 h-px bg-teal-500/40 hidden sm:block shrink-0 mx-2" />
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-24 max-w-[1440px] mx-auto px-6 lg:px-12">
        <div className="text-center mb-16">
          <p className="text-[#d4a017] text-xs font-semibold uppercase tracking-widest mb-4">{t('about_timeline_badge')}</p>
          <h2 className="text-4xl font-bold text-white">{t('about_timeline_title')}</h2>
        </div>
        <div className="relative">
          <div className="absolute left-6 lg:left-1/2 top-0 bottom-0 w-px bg-gradient-to-b from-teal-500/40 via-[#d4a017]/40 to-transparent" />
          <div className="space-y-12">
            {timelineItems.map((item, i) => (
              <div key={item.year} className={`relative flex flex-col lg:flex-row items-start lg:items-center gap-6 ${i % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'}`}>
                {/* Dot */}
                <div className="absolute left-4 lg:left-1/2 -translate-x-1/2 w-4 h-4 rounded-full bg-[#d4a017] border-2 border-[#08080f] z-10" />

                {/* Content */}
                <div className={`ml-14 lg:ml-0 flex-1 ${i % 2 === 0 ? 'lg:pr-16 lg:text-right' : 'lg:pl-16'}`}>
                  <div className="inline-block bg-[#d4a017]/10 border border-[#d4a017]/30 rounded-full px-3 py-1 mb-3">
                    <span className="text-[#d4a017] text-xs font-bold">{item.year}</span>
                  </div>
                  <h3 className="text-white font-bold text-lg mb-2">{item.title}</h3>
                  <p className="text-white/50 text-sm leading-relaxed max-w-sm">{item.desc}</p>
                </div>

                <div className="flex-1 hidden lg:block" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Filosofie */}
      <section className="py-24 bg-gradient-to-b from-[#0a0a14] to-[#08080f]">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <div className="text-center mb-14">
            <p className="text-[#d4a017] text-xs font-semibold uppercase tracking-widest mb-4">{t('about_philosophy_badge')}</p>
            <h2 className="text-4xl font-bold text-white mb-4">{t('about_philosophy_title')}</h2>
            <p className="text-white/50 text-base max-w-xl mx-auto">{t('about_philosophy_sub')}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {philosophy.map(p => (
              <div key={p.title} className="p-6 bg-white/3 border border-white/8 rounded-2xl hover:border-teal-500/20 transition-colors group">
                <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-teal-500/10 border border-teal-500/20 mb-5 group-hover:bg-teal-500/15 transition-colors">
                  <i className={`${p.icon} text-xl text-teal-400`} />
                </div>
                <h3 className="text-white font-bold text-base mb-2">{p.title}</h3>
                <p className="text-white/50 text-sm leading-relaxed">{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Quote */}
      <section className="relative py-28 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=mystical%20dark%20spiritual%20background%20with%20burning%20incense%20smoke%20asafoetida%20hing%20fragrant%20resin%20deep%20dramatic%20atmospheric%20cinematic%20photography%20golden%20smoke%20tendrils%20dark%20moody&width=1920&height=500&seq=zpiritual-quote-bg&orientation=landscape"
            alt=""
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-[#08080f]/80" />
        </div>
        <div className="relative z-10 max-w-3xl mx-auto px-6 lg:px-12 text-center">
          <i className="ri-double-quotes-l text-5xl text-[#d4a017]/40 mb-6 block" />
          <p className="text-white text-xl lg:text-2xl font-light leading-relaxed italic mb-6">{t('about_quote')}</p>
          <p className="text-[#d4a017] text-sm uppercase tracking-widest">— ZpiritualZ</p>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 max-w-[1440px] mx-auto px-6 lg:px-12 text-center">
        <p className="text-[#d4a017] text-xs font-semibold uppercase tracking-widest mb-4">{t('about_cta_badge')}</p>
        <h2 className="text-4xl font-bold text-white mb-5">{t('about_cta_title')}</h2>
        <p className="text-white/55 text-base max-w-xl mx-auto mb-10 leading-relaxed">{t('about_cta_sub')}</p>
        <div className="flex flex-wrap gap-4 justify-center">
          <Link to="/evil-energy-destroy" className="inline-flex items-center gap-2 px-7 py-3.5 bg-gradient-to-r from-teal-500 to-[#d4a017] text-[#08080f] font-bold rounded-full whitespace-nowrap hover:opacity-90 transition-opacity cursor-pointer">
            <i className="ri-shield-star-line" />
            {t('about_cta_ee')}
          </Link>
          <Link to="/shop" className="inline-flex items-center gap-2 px-7 py-3.5 border border-white/20 text-white rounded-full whitespace-nowrap hover:border-white/40 transition-colors cursor-pointer">
            <i className="ri-store-2-line" />
            {t('about_cta_shop')}
          </Link>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
